import { MAX_CYCLES_EXPENSE } from 'templates/Customers/Modals/BodyModalNewChargeOrRecurrence/Components/FrequencyControls'
import * as yup from 'yup'

import { parseMoney, priceConverter } from 'utils/FormatMoney'

const moneyNumber = (requiredMessage: string) =>
  yup
    .number()
    .transform((_value, originalValue) => {
      if (
        originalValue === '' ||
        originalValue === null ||
        originalValue === undefined
      ) {
        return undefined
      }

      const parsed = parseMoney(originalValue)
      return parsed === null ? NaN : parsed
    })
    .typeError(requiredMessage)
    .required(requiredMessage)

export const schemaNewExpense = yup.object({
  payee: yup.object({
    value: yup.string().required('Beneficiário obrigatório').nullable(),
    label: yup.string().required('Beneficiário obrigatório').nullable()
  }),
  enableRateio: yup.boolean().default(false),
  allocations: yup
    .array()
    .of(
      yup.object({
        uuid: yup.string(),
        category: yup.object({
          value: yup.string().required('Categoria obrigatória').nullable(),
          label: yup.string().required('Categoria obrigatória').nullable()
        }),
        costCenter: yup.object({
          value: yup.string().notRequired().nullable(),
          label: yup.string().notRequired().nullable()
        }),
        amount: yup
          .number()
          .nullable()
          .transform((_value, originalValue) => {
            if (originalValue == null || originalValue === '') return null
            const parsed = priceConverter(originalValue as unknown as number)
            return Number.isNaN(parsed) ? null : parsed
          }),
        // Campo apagado chega como "": sem o transform o yup mostrava o erro
        // cru "must be a `number` type ... NaN". Vazio vira null e quem cobra
        // a linha é o 'rateio-linha-com-valor'.
        percentage: yup
          .number()
          .nullable()
          .transform((value, originalValue) =>
            originalValue === '' || Number.isNaN(value) ? null : value
          )
      })
    )
    .min(1, 'É necessário ao menos uma linha de rateio')
    .test(
      'rateio-linha-com-valor',
      'Informe o valor desta linha do rateio',
      function (allocations) {
        // O back exige amount > 0 e percentage > 0 em toda allocation. Uma linha
        // só com categoria passava aqui (a soma ainda fechava com as outras) e
        // o back recusava a despesa inteira.
        if (!this.parent.enableRateio || !allocations) return true
        const emptyIndex = allocations.findIndex((a) => !(Number(a.amount) > 0))
        if (emptyIndex === -1) return true
        return this.createError({ path: `allocations[${emptyIndex}].amount` })
      }
    )
    .test(
      'rateio-fecha',
      'A soma do rateio deve ser igual ao valor total da despesa',
      function (allocations) {
        if (!allocations || allocations.length <= 1) return true
        const expenses = (this.parent.expenses ?? []) as Array<{
          total?: number
        }>
        const total = expenses.reduce(
          (acc, e) => acc + (Number(e.total) || 0),
          0
        )
        const sum = allocations.reduce(
          (acc, a) => acc + (Number(a.amount) || 0),
          0
        )
        return Math.abs(sum - total) < 0.01
      }
    )
    .test(
      'rateio-sem-combo-repetido',
      'Há linhas de rateio com a mesma categoria e centro de custo',
      function (allocations) {
        if (!allocations) return true
        const seen = new Set<string>()
        for (const a of allocations) {
          const key = `${a.category?.value || ''}::${a.costCenter?.value || ''}`
          if (seen.has(key)) return false
          seen.add(key)
        }
        return true
      }
    ),
  competence: yup
    .date()
    .nullable()
    .notRequired()
    .transform((_value, originalValue) => {
      if (!originalValue) return null // trata '', undefined, null
      const parsed = new Date(originalValue)
      return isNaN(parsed.getTime()) ? null : parsed
    }),

  dueDate: yup
    .date()
    .typeError('Insira uma data válida')
    .required('Data de vencimento obrigatória'),
  costType: yup.number().required('Tipo é obrigatório'),
  accountsPayableFrequency: yup.number(),
  expenses: yup
    .array()
    .of(
      yup.object({
        description: yup.string().required('Item obrigatório'),
        price: moneyNumber('Informe o valor da despesa.'),
        // Campo apagado chega como "": sem o transform o yup mostra o erro cru
        // "must be a `number` type ... NaN" em vez da mensagem de obrigatório.
        quantity: yup
          .number()
          .transform((value, originalValue) =>
            originalValue === '' || originalValue === null ? undefined : value
          )
          .typeError('Informe a quantidade da despesa.')
          .required('Informe a quantidade da despesa.'),
        total: moneyNumber('O total da despesa é obrigatório.'),
        uuid: yup.string()
      })
    )
    .min(1, 'É necessário ao mínimo uma despesa'),
  description: yup.string(),
  repeat: yup.boolean().default(false),
  frequency: yup.number().when('repeat', {
    is: true,
    then: yup.number().required('Frequência obrigatória'),
    otherwise: yup.number().notRequired().nullable()
  }),
  totalCycles: yup.number().when('repeat', {
    is: true,
    then: yup.number().required('Total de ciclos é obrigatório.'),
    otherwise: yup.number().notRequired().nullable()
  }),
  totalCyclesCustom: yup.string().when('totalCycles', {
    is: 13,
    otherwise: yup.string(),
    then: yup
      .string()
      .required('Total de ciclos personalizado é obrigatório.')
      .test(
        'min-cycles',
        'O total de ciclos deve ser maior que 1.',
        (value) => Number(value) > 1
      )
      .test(
        'max-cycles',
        `O total de ciclos não pode passar de ${MAX_CYCLES_EXPENSE} meses (15 anos).`,
        (value) => Number(value) <= MAX_CYCLES_EXPENSE
      )
  }),
  markAsPaid: yup.boolean().default(false),
  source: yup
    .object({
      value: yup.string().nullable(),
      label: yup.string().nullable()
    })
    .nullable(),
  datePaid: yup
    .date()
    .typeError('Insira uma data válida')
    .when('markAsPaid', {
      is: true,
      then: (schema) => schema.required('Data de pagamento obrigatória'),
      otherwise: (schema) => schema.nullable().notRequired()
    }),
  amountPaid: yup
    .number()
    .transform((_value, originalValue) => {
      if (
        originalValue === '' ||
        originalValue === null ||
        originalValue === undefined
      ) {
        return null
      }

      const parsed = parseMoney(originalValue)
      return parsed === null ? null : parsed
    })
    .when('markAsPaid', {
      is: true,
      then: (schema) => schema.required('Valor pago obrigatório'),
      otherwise: (schema) => schema.nullable().notRequired()
    }),

  documentUuid: yup.string(),
  documentUuids: yup.array().of(yup.string().required()),
  tags: yup
    .array()
    .of(
      yup.object({
        id: yup.string().required('Identificador da tag é obrigatório.'),
        name: yup.string().required('Nome da tag é obrigatório.'),
        color: yup.string()
      })
    )
    .default([])
})

export type NewExpenseSchema = yup.InferType<typeof schemaNewExpense>
